import React, { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { db, supabase } from '../supabase'
import { 
  Trophy, 
  Heart, 
  Search, 
  Filter,
  TrendingUp,
  Star,
} from 'lucide-react'

const CommunityPage = () => {
  const { user, profile } = useAuth()
  const [topRewrites, setTopRewrites] = useState([])
  const [leaderboard, setLeaderboard] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedFilter, setSelectedFilter] = useState('all')
  const [loading, setLoading] = useState(true)
  const [reactions, setReactions] = useState({})
  const emojis = ['😂', '😍', '👏', '🤯', '🥲', '🔥']

  const filters = [
    { value: 'all', label: 'All', emoji: '🌟' },
    { value: 'funny', label: 'Funny', emoji: '😂' },
    { value: 'poetic', label: 'Poetic', emoji: '📝' },
    { value: 'sad', label: 'Sad', emoji: '😢' },
    { value: 'weird', label: 'Weird', emoji: '🤪' },
    { value: 'random', label: 'Random', emoji: '🎲' }
  ]

  const loadLeaderboard = useCallback(async () => {
    try {
      // First get users with their rewrite counts
      const { data: users, error: usersError } = await supabase
        .from('users')
        .select(`
          id,
          name,
          avatar_url
        `)
        .limit(10)
      
      if (usersError) throw usersError
      
      // Get rewrite counts for each user
      const { data: rewriteCounts, error: rewriteError } = await supabase
        .from('rewrites')
        .select('rewriter_id')
        .in('rewriter_id', users.map(u => u.id))
      
      if (rewriteError) throw rewriteError
      
      // Count rewrites per user
      const rewriteCountMap = rewriteCounts.reduce((acc, rewrite) => {
        acc[rewrite.rewriter_id] = (acc[rewrite.rewriter_id] || 0) + 1
        return acc
      }, {})
      
      // Combine user data with rewrite counts
      const leaderboardData = users.map(user => ({
        ...user,
        rewrites: rewriteCountMap[user.id] || 0
      })).sort((a, b) => b.rewrites - a.rewrites)
      
      return leaderboardData
    } catch (error) {
      console.error('Error loading leaderboard:', error)
      return []
    }
  }, [])

  const loadCommunityData = useCallback(async () => {
    try {
      setLoading(true)
      const [rewrites, leaderboardData] = await Promise.all([
        db.getTopRewrites().catch(error => {
          console.error('Error loading rewrites:', error)
          return []
        }),
        loadLeaderboard().catch(error => {
          console.error('Error loading leaderboard:', error)
          return []
        })
      ])
      setTopRewrites(rewrites || [])
      setLeaderboard(leaderboardData || [])
    } catch (error) {
      console.error('Error loading community data:', error)
      setTopRewrites([])
      setLeaderboard([])
    } finally {
      setLoading(false)
    }
  }, [loadLeaderboard])

  useEffect(() => {
    if (user) {
      loadCommunityData()
    }
  }, [user, loadCommunityData])

  useEffect(() => {
    // Realtime subscription for rewrites and votes (mocked for now)
    const rewritesSub = supabase
      .channel('realtime-rewrites')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'rewrites' }, payload => {
        // In real implementation, reload rewrites/leaderboard
        setTopRewrites(rws => [payload.new, ...rws]);
      })
      .subscribe();
    const votesSub = supabase
      .channel('realtime-votes')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'votes' }, payload => {
        // In real implementation, reload leaderboard
        setLeaderboard(lb => lb.map(u => u.id === payload.new.rewrite_owner_id ? { ...u, votes: (u.votes || 0) + 1 } : u));
      })
      .subscribe();
    return () => {
      supabase.removeChannel(rewritesSub);
      supabase.removeChannel(votesSub);
    };
  }, []);

  const handleVote = async (rewriteId) => {
    try {
      const result = await db.voteRewrite(rewriteId, user.id)
      console.log('Vote result:', result)
      await loadCommunityData() // Refresh data
    } catch (error) {
      console.error('Error voting:', error)
      // Don't show alert for database structure issues
      if (!error.message?.includes('relation "votes" does not exist')) {
        alert('Failed to vote. Please try again.')
      }
    }
  }

  const handleAddFriend = async (friendId) => {
    try {
      // This would be implemented with a friends system
      alert('Friend system coming soon!')
    } catch (error) {
      console.error('Error adding friend:', error)
    }
  }

  const filteredRewrites = topRewrites.filter(rewrite => {
    const matchesSearch = searchQuery === '' || 
      rewrite.rewriter.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rewrite.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
      rewrite.reminder.text.toLowerCase().includes(searchQuery.toLowerCase())
    
    const matchesFilter = selectedFilter === 'all' || 
      rewrite.reminder.mood === selectedFilter
    
    return matchesSearch && matchesFilter
  })

  // Mocked current user's friends
  const myFriends = profile?.friends || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 md:py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
          Community 🌍
        </h1>
        <p className="text-gray-600 text-lg">
          Discover amazing rewrites and meet fellow Strangeminders
        </p>
      </div>

      {/* Search and Filter */}
      <div className="card mb-8">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search rewrites, users, or reminders..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field pl-10"
            />
          </div>
          
          <div className="flex items-center space-x-2">
            <Filter size={20} className="text-gray-500" />
            <select
              value={selectedFilter}
              onChange={(e) => setSelectedFilter(e.target.value)}
              className="input-field"
            >
              {filters.map(filter => (
                <option key={filter.value} value={filter.value}>
                  {filter.emoji} {filter.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Leaderboard */}
        <div className="lg:col-span-1">
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <Trophy className="mr-2" size={20} />
              Top Rewriters
            </h2>

            {leaderboard.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">🏆</div>
                <p className="text-gray-600">No leaderboard data yet.</p>
              </div>
            ) : (
              <ul>
                {leaderboard.map(user => (
                  <li key={user.id} className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <img src={user.avatar_url} alt={user.name} className="w-8 h-8 rounded-full border-2 border-primary-200" />
                      <span className="font-medium">{user.name}</span>
                    </div>
                    {!myFriends.some(f => f.id === user.id) && (
                      <button className="btn-primary text-xs py-1 px-3" onClick={() => handleAddFriend(user.id)}>
                        Add Friend
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Public Wall */}
        <div className="lg:col-span-2">
          <div className="card">
            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
              <TrendingUp className="mr-2" size={20} />
              Best Rewrites
            </h2>

            {filteredRewrites.length === 0 ? (
              <div className="text-center py-8">
                <div className="text-4xl mb-4">💭</div>
                <p className="text-gray-600 mb-2">No rewrites found!</p>
                <p className="text-sm text-gray-500">
                  {searchQuery ? 'Try adjusting your search or filter.' : 'Be the first to create amazing rewrites!'}
                </p>
              </div>
            ) : (
              <div className="space-y-6">
                {filteredRewrites.map((rewrite) => (
                  <div key={rewrite.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center space-x-3">
                        {rewrite.rewriter.avatar_url ? (
                          <img
                            src={rewrite.rewriter.avatar_url}
                            alt={rewrite.rewriter.name}
                            className="w-12 h-12 rounded-full"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-primary-100 rounded-full flex items-center justify-center">
                            <span className="text-primary-600 font-medium">
                              {rewrite.rewriter.name?.charAt(0) || '?'}
                            </span>
                          </div>
                        )}
                        
                        <div>
                          <p className="font-semibold text-gray-800">
                            {rewrite.rewriter.name}
                          </p>
                          <p className="text-sm text-gray-500">
                            rewrote "{rewrite.reminder.text}"
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleVote(rewrite.id)}
                          className="flex items-center space-x-1 px-3 py-1 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-colors"
                        >
                          <Heart size={14} />
                          <span className="text-sm font-medium">
                            {rewrite.votes || 0}
                          </span>
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-gradient-to-r from-primary-50 to-lavender-50 rounded-lg p-4 mb-3">
                      <p className="text-lg text-gray-800 italic">
                        "{rewrite.text}"
                      </p>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-gray-500">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center">
                          <Star size={14} className="mr-1" />
                          {rewrite.reminder.mood}
                        </span>
                        <span>
                          {new Date(rewrite.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      
                      <button
                        onClick={() => handleAddFriend(rewrite.rewriter.id)}
                        className="text-primary-600 hover:text-primary-700 font-medium"
                      >
                        Follow
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Community Stats */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card text-center">
          <div className="text-3xl mb-2">👥</div>
          <h3 className="font-semibold text-gray-800 mb-1">Active Users</h3>
          <p className="text-2xl font-bold text-primary-600">
            {leaderboard.length}+
          </p>
        </div>
        
        <div className="card text-center">
          <div className="text-3xl mb-2">💭</div>
          <h3 className="font-semibold text-gray-800 mb-1">Total Rewrites</h3>
          <p className="text-2xl font-bold text-primary-600">
            {topRewrites.length}+
          </p>
        </div>
        
        <div className="card text-center">
          <div className="text-3xl mb-2">❤️</div>
          <h3 className="font-semibold text-gray-800 mb-1">Total Votes</h3>
          <p className="text-2xl font-bold text-primary-600">
            {topRewrites.reduce((sum, r) => sum + (r.votes || 0), 0)}+
          </p>
        </div>
        
        <div className="card text-center">
          <div className="text-3xl mb-2">🏆</div>
          <h3 className="font-semibold text-gray-800 mb-1">Achievements</h3>
          <p className="text-2xl font-bold text-primary-600">
            {leaderboard.filter(u => u.achievements?.length > 0).length}+
          </p>
        </div>
      </div>

      {/* Public Wall of Rewrites */}
      <div className="card mt-8">
        <h2 className="text-xl font-semibold mb-4 flex items-center">🌟 Great Rewrites</h2>
        {topRewrites.length === 0 ? (
          <p className="text-gray-500">No rewrites yet. Be the first to add one!</p>
        ) : (
          <ul className="space-y-6">
            {topRewrites.map(rw => (
              <li key={rw.id} className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <img src={rw.rewriter.avatar_url} alt={rw.rewriter.name} className="w-8 h-8 rounded-full border-2 border-primary-200" />
                  <span className="font-medium">{rw.rewriter.name}</span>
                </div>
                <p className="mb-2 text-gray-800">{rw.text}</p>
                <div className="flex gap-2 items-center mt-2">
                  {emojis.map(emoji => (
                    <button
                      key={emoji}
                      className="text-xl hover:scale-125 transition-transform p-1 rounded hover:bg-gray-100"
                      onClick={(e) => {
                        setReactions(r => ({ 
                          ...r, 
                          [rw.id]: { 
                            ...(r[rw.id] || {}), 
                            [emoji]: ((r[rw.id]?.[emoji] || 0) + 1) 
                          } 
                        }));
                        // Show feedback
                        const button = e.target;
                        button.style.transform = 'scale(1.5)';
                        setTimeout(() => {
                          button.style.transform = 'scale(1)';
                        }, 200);
                      }}
                    >
                      {emoji} <span className="text-xs">{reactions[rw.id]?.[emoji] || ''}</span>
                    </button>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

export default CommunityPage 